(() => {
  const bundlePropertyKeys = ['_bundleId', '_bundle_id'];
  const cartPageSelector = '[data-section-type="cart"]';
  const drawerSelector = '[data-custom-cart-drawer]';
  const internalRequestHeader = 'X-Theme-Bundle-Sync';
  let reconciliationRequest = null;
  let reconciliationQueued = false;
  let cartPageMutationRequest = null;

  const getCartRoot = () => {
    const root = window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getCartUrl = (path) => `${getCartRoot()}cart${path === 'js' ? '.js' : `/${path}`}`;

  const getBundleId = (line) => {
    const properties = line?.properties;
    if (!properties || typeof properties !== 'object') return '';

    for (const propertyKey of bundlePropertyKeys) {
      const value = properties[propertyKey];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }

    return '';
  };

  const getCartItems = (cart) => (Array.isArray(cart?.items) ? cart.items : []);

  const getBundleGroups = (cart) => {
    const groups = new Map();

    getCartItems(cart).forEach((line) => {
      const bundleId = getBundleId(line);
      if (!bundleId) return;

      if (!groups.has(bundleId)) groups.set(bundleId, []);
      groups.get(bundleId).push(line);
    });

    return groups;
  };

  const getLineByKey = (cart, lineKey) => getCartItems(cart).find((line) => line.key === lineKey);

  const setGroupQuantity = (updates, lines, quantity) => {
    lines.forEach((line) => {
      if (line.key) updates[line.key] = quantity;
    });
  };

  const buildReconciliationUpdates = (cart) => {
    const updates = {};

    getBundleGroups(cart).forEach((lines) => {
      if (lines.length === 1) {
        setGroupQuantity(updates, lines, 0);
        return;
      }

      const quantities = lines
        .map((line) => Number.parseInt(line.quantity, 10))
        .filter((quantity) => Number.isFinite(quantity) && quantity >= 0);
      const hasDifferentQuantities = new Set(quantities).size > 1;

      if (hasDifferentQuantities) {
        setGroupQuantity(updates, lines, Math.min(...quantities));
      }
    });

    return updates;
  };

  const buildLineMutationUpdates = (cart, lineKey, requestedQuantity) => {
    const updates = buildReconciliationUpdates(cart);
    const line = getLineByKey(cart, lineKey);
    if (!line || !line.key) return updates;

    const bundleId = getBundleId(line);
    if (!bundleId) {
      updates[line.key] = Math.max(0, Number.parseInt(requestedQuantity, 10) || 0);
      return updates;
    }

    const group = getBundleGroups(cart).get(bundleId) || [];
    if (group.length === 1) {
      setGroupQuantity(updates, group, 0);
      return updates;
    }

    setGroupQuantity(updates, group, Math.max(0, Number.parseInt(requestedQuantity, 10) || 0));
    return updates;
  };

  const hasUpdates = (updates) => Object.keys(updates || {}).length > 0;

  const readCart = async () => {
    const response = await fetch(getCartUrl('js'), {headers: {Accept: 'application/json'}});
    if (!response.ok) throw new Error('Unable to refresh cart. Please try again.');
    return response.json();
  };

  const updateCart = async (updates, options = {}) => {
    if (!hasUpdates(updates)) return null;

    const payload = {updates};
    if (options.sections) payload.sections = options.sections;
    if (options.sectionsUrl) payload.sections_url = options.sectionsUrl;

    const response = await fetch(getCartUrl('update.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [internalRequestHeader]: '1',
      },
      body: JSON.stringify(payload),
    });
    const responseData = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(responseData.description || responseData.message || 'Unable to update cart. Please try again.');
    }

    return responseData;
  };

  const reconcile = async (cart, options = {}) => {
    const updates = buildReconciliationUpdates(cart);
    if (!hasUpdates(updates)) return {cart, responseData: null};

    const responseData = await updateCart(updates, options);
    return {cart: responseData, responseData};
  };

  const dispatchCartChange = (cart) => {
    document.dispatchEvent(
      new CustomEvent('theme:cart:change', {
        detail: {cart, bundleSynchronized: true},
        bubbles: true,
      }),
    );
  };

  const isCartPayload = (payload) => Boolean(payload && Array.isArray(payload.items));

  const queueReconciliation = () => {
    reconciliationQueued = true;
    if (reconciliationRequest) return reconciliationRequest;

    reconciliationRequest = (async () => {
      while (reconciliationQueued) {
        reconciliationQueued = false;

        const cart = await readCart();
        if (!hasUpdates(buildReconciliationUpdates(cart))) continue;

        const {cart: synchronizedCart} = await reconcile(cart);
        if (!synchronizedCart) continue;

        dispatchCartChange(synchronizedCart);
        document.dispatchEvent(new CustomEvent('theme:cart:reload', {bubbles: true}));
      }
    })()
      .catch(() => {})
      .finally(() => {
        reconciliationRequest = null;
        if (reconciliationQueued) queueReconciliation();
      });

    return reconciliationRequest;
  };

  const getRequestUrl = (input) => {
    const rawUrl = typeof input === 'string' ? input : input?.url;
    if (!rawUrl) return null;

    try {
      return new URL(rawUrl, window.location.origin);
    } catch {
      return null;
    }
  };

  const hasInternalRequestHeader = (input, init = {}) => {
    try {
      const requestHeaders = typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined;
      const headers = new Headers(init.headers || requestHeaders);
      return headers.get(internalRequestHeader) === '1';
    } catch {
      return false;
    }
  };

  const isObservedCartMutation = (input, init = {}) => {
    const method = String(init.method || input?.method || 'GET').toUpperCase();
    if (method !== 'POST' || hasInternalRequestHeader(input, init)) return false;

    const url = getRequestUrl(input);
    return Boolean(url && url.origin === window.location.origin && /(?:^|\/)cart\/(?:add|change|update|clear)\.js$/.test(url.pathname));
  };

  const observeCartResponse = (payload) => {
    if (isCartPayload(payload)) queueReconciliation();
  };

  const installFetchInterceptor = () => {
    if (typeof window.fetch !== 'function' || window.fetch.__themeBundleSyncWrapped) return;

    const originalFetch = window.fetch;
    const wrappedFetch = function (input, init) {
      const shouldObserve = isObservedCartMutation(input, init);
      const request = originalFetch.apply(this, arguments);

      if (!shouldObserve) return request;

      return request.then((response) => {
        if (response.ok) {
          response
            .clone()
            .json()
            .then(observeCartResponse)
            .catch(() => {});
        }

        return response;
      });
    };

    wrappedFetch.__themeBundleSyncWrapped = true;
    window.fetch = wrappedFetch;
  };

  const installXhrInterceptor = () => {
    const xhrPrototype = window.XMLHttpRequest?.prototype;
    if (!xhrPrototype || xhrPrototype.__themeBundleSyncWrapped) return;

    const originalOpen = xhrPrototype.open;
    const originalSend = xhrPrototype.send;

    xhrPrototype.open = function (method, url) {
      this.__themeBundleSyncCartMutation = isObservedCartMutation({method, url});
      return originalOpen.apply(this, arguments);
    };

    xhrPrototype.send = function () {
      if (this.__themeBundleSyncCartMutation) {
        this.addEventListener('load', () => {
          if (this.status < 200 || this.status >= 300) return;

          let payload = this.responseType === 'json' ? this.response : null;
          if (!payload) {
            try {
              payload = JSON.parse(this.responseText);
            } catch {
              payload = null;
            }
          }

          observeCartResponse(payload);
        }, {once: true});
      }

      return originalSend.apply(this, arguments);
    };

    xhrPrototype.__themeBundleSyncWrapped = true;
  };

  const installCartMutationInterceptors = () => {
    installFetchInterceptor();
    installXhrInterceptor();
  };

  const setCartPageLoading = (isLoading) => {
    const cartPage = document.querySelector(cartPageSelector);
    const loader = cartPage?.querySelector('[data-cart-loading]');
    loader?.classList.toggle('cart--loading', isLoading);
  };

  const showCartPageError = (message) => {
    const cartPage = document.querySelector(cartPageSelector);
    const error = cartPage?.querySelector('[data-form-errors]');
    if (!error) return;

    error.textContent = message;
    error.style.display = message ? '' : 'none';
  };

  const handleCartPageMutation = (event, lineKey, quantity) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (cartPageMutationRequest) return;

    setCartPageLoading(true);
    showCartPageError('');
    cartPageMutationRequest = readCart()
      .then((cart) => {
        const updates = buildLineMutationUpdates(cart, lineKey, quantity);
        return updateCart(updates);
      })
      .then((cart) => {
        if (!cart) return;
        dispatchCartChange(cart);
        document.dispatchEvent(new CustomEvent('theme:cart:reload', {bubbles: true}));
      })
      .catch((error) => {
        showCartPageError(error.message || 'Unable to update cart. Please try again.');
      })
      .finally(() => {
        cartPageMutationRequest = null;
        setCartPageLoading(false);
      });
  };

  const initCartPageHandlers = () => {
    const cartPage = document.querySelector(cartPageSelector);
    if (!cartPage) return;

    document.addEventListener(
      'change',
      (event) => {
        const target = event.target instanceof Element ? event.target.closest('[data-update-cart]') : null;
        if (!target || !cartPage.contains(target) || target.closest(drawerSelector)) return;

        const quantity = Number.parseInt(target.value, 10);
        handleCartPageMutation(event, target.dataset.updateCart, Number.isFinite(quantity) ? quantity : 0);
      },
      true,
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target.closest('[data-remove-key]') : null;
        if (!target || !cartPage.contains(target) || target.closest(drawerSelector)) return;

        handleCartPageMutation(event, target.dataset.removeKey, 0);
      },
      true,
    );
  };

  const reconcileFromEvent = (event) => {
    if (event.detail?.bundleSynchronized || !event.detail?.cart) return;

    const cart = event.detail.cart;
    if (!hasUpdates(buildReconciliationUpdates(cart))) return;

    queueReconciliation();
  };

  window.CartBundleSync = {
    getBundleId,
    getBundleGroups,
    buildReconciliationUpdates,
    buildLineMutationUpdates,
    hasUpdates,
    readCart,
    updateCart,
    reconcile,
    dispatchCartChange,
  };

  initCartPageHandlers();
  document.addEventListener('theme:cart:change', reconcileFromEvent);
  installCartMutationInterceptors();
})();
